import { useLayoutEffect, useState, type RefObject } from 'react';

export interface PopoverPos {
  top: number;
  left: number;
  maxH: number;
  /** The anchor field's width, for a menu that wants to be at least as wide as its field. */
  anchorW: number;
}

/**
 * Place a portaled dropdown against the window while keeping it PINNED to its
 * field — so a menu "conforms to the open card window and adjusts its position
 * while still attached to the field" (Vinnie, 17 Oct), instead of spilling off
 * the card and clipping, or scrolling inside itself.
 *
 * The popover is rendered in a portal at <body> (so no modal or panel can clip
 * it) and positioned by hand: opened BELOW the field, FLIPPED ABOVE when there is
 * no room below and more room up top, and SHIFTED so it never runs off an edge —
 * right-aligned to the field when a left-aligned menu would overflow the right,
 * then clamped wholly inside the window. Its height is capped to the window, so a
 * scrollbar appears only on a window too short to hold the list at all (the last
 * resort), never in the normal case. A ResizeObserver re-places it when the list
 * filters to a different height; scroll/resize keep it tracking the field.
 *
 * Shared by every field dropdown — Area, Epic, Labels and the colour picker — so
 * they open and fit identically. Mirrors CalendarPopover, which does the same for
 * the date field.
 *
 * `anchorRef` is the field box the menu hangs from; `popRef` is the portaled
 * popover. `boundaryRef` is an optional element whose top/bottom cap the menu's
 * VERTICAL range instead of the window — pass the card's scroll area (its
 * `.modal-body`) for a control that sits low in a modal, so the menu flips to fit
 * the CARD rather than extending down the screen into the footer below it. When
 * it is omitted (or its element is absent) the window is the bound, as before.
 * Returns null until measured (render the popover hidden until then).
 */
export function useAnchoredPopover(
  open: boolean,
  anchorRef: RefObject<HTMLElement | null>,
  popRef: RefObject<HTMLElement | null>,
  boundaryRef?: RefObject<HTMLElement | null>,
): PopoverPos | null {
  const [pos, setPos] = useState<PopoverPos | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const place = () => {
      const a = anchorRef.current?.getBoundingClientRect();
      const el = popRef.current;
      if (!a || !el) return;
      const gap = 6;
      const pad = 8;
      // Vertical bounds: the boundary element when given (e.g. the card's scroll
      // area), else the window — so a menu low in a modal fits the card, not just
      // the screen.
      const b = boundaryRef?.current?.getBoundingClientRect();
      const topLim = b ? b.top + pad : pad;
      const botLim = b ? b.bottom - pad : window.innerHeight - pad;
      const maxH = Math.max(160, botLim - topLim);
      el.style.maxHeight = `${maxH}px`; // cap first so offsetHeight reflects the capped height
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      // Horizontal: open toward the side the field sits on, so the menu stays on
      // the card instead of overhanging into the margin — a field whose centre is
      // past the window's centre (a right-hand column) right-aligns to its field
      // and opens leftward; a left-hand field left-aligns. Then force right-align
      // if a left-aligned menu would still run off the right edge, and finally
      // clamp wholly inside the window.
      let left = a.left;
      if (a.left + a.width / 2 > window.innerWidth / 2) left = a.right - w;
      if (left + w > window.innerWidth - pad) left = a.right - w;
      left = Math.min(Math.max(pad, left), window.innerWidth - w - pad);
      // Vertical: below the field, flipped above when it would run past the bottom
      // bound and there is more room up top; then clamped inside the bounds.
      const below = a.bottom + gap;
      const above = a.top - gap - h;
      let top = below;
      if (below + h > botLim && (above >= topLim || a.top - topLim > botLim - a.bottom)) top = above;
      top = Math.min(Math.max(topLim, top), botLim - h);
      setPos({ top, left, maxH, anchorW: Math.round(a.width) });
    };
    place();
    const el = popRef.current;
    const ro = el && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(place) : null;
    ro?.observe(el as Element);
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      ro?.disconnect();
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, anchorRef, popRef, boundaryRef]);

  return pos;
}
